import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-maroon flex items-center justify-center">
                <span className="text-white font-heading font-bold text-lg">b8</span>
              </div>
              <span className="font-heading font-bold text-xl text-maroon">batchm8s</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-gray-600">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-maroon/10 rounded-xl">
              <FileText className="h-8 w-8 text-maroon" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-gray-900">Terms of Service</h1>
              <p className="text-gray-500">Last updated: February 20, 2026</p>
            </div>
          </div>

          <div className="prose prose-gray max-w-none">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using the batchm8s platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.
            </p>

            <h2>2. Description of Service</h2>
            <p>
              batchm8s is a reunion management platform that enables organizers to plan and manage alumni reunions, and allows attendees to participate in reunion events. Our services include:
            </p>
            <ul>
              <li>Event creation and management</li>
              <li>Member registration and directory</li>
              <li>Payment tracking and verification</li>
              <li>Announcements and communications</li>
              <li>Committee management</li>
              <li>Document sharing</li>
              <li>In-app messaging</li>
            </ul>

            <h2>3. User Accounts</h2>
            <p>
              To use certain features of the Service, you must register for an account. You agree to:
            </p>
            <ul>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Keep your password secure and confidential</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>

            <h2>4. User Roles and Responsibilities</h2>
            <h3>4.1 Organizers</h3>
            <p>
              Organizers are responsible for managing their events, including setting up payment methods, verifying payments, and ensuring accurate information is provided to attendees.
            </p>
            
            <h3>4.2 Attendees</h3>
            <p>
              Attendees agree to provide accurate personal information and follow the guidelines set by event organizers.
            </p>

            <h2>5. Payments and Subscriptions</h2>
            <p>
              Organizers may purchase subscription plans to access premium features. All payments are processed through secure third-party payment providers. Refunds are subject to our refund policy.
            </p>
            <ul>
              <li>Subscription fees are billed according to the selected plan</li>
              <li>Payment methods configured by organizers are their responsibility</li>
              <li>We do not store or process payment card details directly</li>
            </ul>

            <h2>6. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any unlawful purpose</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Upload malicious content or viruses</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Impersonate any person or entity</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Use the Service to send spam or unsolicited messages</li>
            </ul>

            <h2>7. Intellectual Property</h2>
            <p>
              The Service and its original content, features, and functionality are owned by batchm8s and are protected by international copyright, trademark, and other intellectual property laws.
            </p>

            <h2>8. User Content</h2>
            <p>
              You retain ownership of content you submit to the Service. By submitting content, you grant us a non-exclusive, worldwide, royalty-free license to use, display, and distribute your content in connection with the Service.
            </p>

            <h2>9. Privacy</h2>
            <p>
              Your use of the Service is also governed by our <Link to="/privacy" className="text-maroon hover:underline">Privacy Policy</Link>. Please review our Privacy Policy to understand our practices.
            </p>

            <h2>10. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
            </p>

            <h2>11. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, BATCHM8S SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES RESULTING FROM YOUR USE OF THE SERVICE.
            </p>

            <h2>12. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless batchm8s and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Service or violation of these Terms.
            </p>

            <h2>13. Termination</h2>
            <p>
              We may terminate or suspend your account and access to the Service immediately, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties.
            </p>

            <h2>14. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of significant changes by posting a notice on our website or sending an email. Continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>

            <h2>15. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which batchm8s operates, without regard to its conflict of law provisions.
            </p>

            <h2>16. Contact Us</h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <ul>
              <li>Email: info@batchm8s.com</li>
            </ul>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-500 text-sm">© 2026 batchm8s. Made with ❤️ for alumni everywhere.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
